import React from "react";

interface LoaderProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  text?: string;
}

/**
 * Beautiful custom Tailwind loader component
 * Use for full page or container loading states
 */
export default function Loader({ size = "md", className = "", text }: LoaderProps) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-16 h-16",
    lg: "w-24 h-24",
  };

  const ringSizeClasses = {
    sm: "w-8 h-8 border-2",
    md: "w-16 h-16 border-3",
    lg: "w-24 h-24 border-4",
  };

  return (
    <div className={`flex flex-col items-center justify-center gap-4 ${className}`}>
      <div className="relative">
        {/* Outer spinning ring */}
        <div
          className={`${ringSizeClasses[size]} border-t-[var(--nba-green)] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin`}
          style={{
            animationDuration: "1s",
          }}
        />
        {/* Inner spinning ring */}
        <div
          className={`${ringSizeClasses[size]} border-t-[var(--nba-green)] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin absolute top-0 left-0 opacity-50`}
          style={{
            animationDuration: "0.7s",
            animationDirection: "reverse",
          }}
        />
        {/* Center dot */}
        <div
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${sizeClasses[size]} bg-[var(--nba-green)] rounded-full animate-pulse`}
        />
      </div>
      {text && (
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 animate-pulse">
          {text}
        </p>
      )}
    </div>
  );
}

/**
 * Miniature loader for buttons
 * Use inside buttons to show loading state
 */
export function ButtonLoader({ size = "sm", color = "white" }: { size?: "sm" | "md"; color?: "white" | "green" }) {
  const sizeClasses = {
    sm: "w-4 h-4 border-2",
    md: "w-5 h-5 border-2",
  };

  const borderColor = color === "white" 
    ? "border-t-white border-r-transparent border-b-transparent border-l-transparent"
    : "border-t-[var(--nba-green)] border-r-transparent border-b-transparent border-l-transparent";

  return (
    <span className="inline-flex items-center justify-center mr-2">
      <span
        className={`${sizeClasses[size]} ${borderColor} rounded-full animate-spin`}
        style={{
          animationDuration: "0.8s",
        }}
      />
    </span>
  );
}

